import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, FileDown, FolderInput, Trash2, MoreVertical } from 'lucide-react';
import api, { API } from '@/lib/api';
import { formatRp, formatDate, QUOTE_STATUSES } from '@/lib/format';
import { QuoteBadge } from '@/components/StatusBadge';
import { ListSkeleton } from '@/components/Skeletons';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function Penawaran() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const r = await api.get('/quotes', { params });
      setItems(r.data);
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const openPdf = (id) => window.open(`${API}/quotes/${id}/pdf`, '_blank');

  const convert = async (id) => {
    try {
      await api.post(`/quotes/${id}/convert`);
      toast.success('Penawaran dikonversi menjadi proyek');
      navigate('/proyek');
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal konversi'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Hapus penawaran ini?')) return;
    try { await api.delete(`/quotes/${id}`); toast.success('Penawaran dihapus'); load(); } catch { toast.error('Gagal menghapus'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-xl font-bold">Penawaran / RAB</h1>
          <p className="text-sm text-muted-foreground">Buat & kelola penawaran pelanggan.</p>
        </div>
        <Button data-testid="rab-buat-penawaran-button" onClick={() => navigate('/penawaran/baru')} className="rounded-xl"><Plus className="mr-1.5 h-4 w-4" /> Buat</Button>
      </div>

      <Select value={filter} onValueChange={setFilter}>
        <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Semua Status</SelectItem>
          {QUOTE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>

      {loading ? <ListSkeleton /> : items.length === 0 ? (
        <EmptyState icon={FileText} title="Belum ada penawaran" description="Coba buat dari deskripsi singkat dengan bantuan AI." actionLabel="Buat Penawaran" onAction={() => navigate('/penawaran/baru')} testid="penawaran-empty-add" />
      ) : (
        <div className="space-y-2.5">
          {items.map((q) => (
            <div key={q.id} data-testid="penawaran-row" className="flex items-center gap-3 rounded-xl border bg-card p-4 transition-shadow hover:shadow-[var(--shadow-md)]">
              <button onClick={() => navigate(`/penawaran/${q.id}`)} className="min-w-0 flex-1 text-left">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{q.nama_proyek}</span>
                  <QuoteBadge status={q.status} />
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">{q.nomor} · {q.customer_name || 'Tanpa pelanggan'} · {formatDate(q.created_at)}</div>
                <div className="mt-1 font-display text-base font-semibold text-primary tabular-nums">{formatRp(q.grand_total)}</div>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"><MoreVertical className="h-5 w-5" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(`/penawaran/${q.id}`)}><FileText className="mr-2 h-4 w-4" /> Buka / Edit</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openPdf(q.id)}><FileDown className="mr-2 h-4 w-4" /> Export PDF</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => convert(q.id)}><FolderInput className="mr-2 h-4 w-4" /> Jadikan Proyek</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => remove(q.id)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Hapus</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
